import { ApiError, Box, CenteredLoader, Flex, Separator, SubTitle, Text } from "@shared/primitive-components"
import { formatPercentage } from "@shared/utils/formatters/formatPercentage"
import { TripVehicle, useGetTripVehicle } from "../../../api/insights"
import { MaybeObfuscatedValue } from "../../../components/Obfuscated"
import { usePolicyAndFleet } from "../../../contexts"
import { calculateScoreChange } from "../../../utils"
import { vehicleDetailsStyles as styles } from "../../styles"
import { Score } from "."

export const VEHICLE_DETAILS_DTI = {
  VRN: `vehicle_details_vrn`,
  MAKE_MODEL: `vehicle_details_model`,
  MILES_DRIVEN: `vehicle_details_miles_driven`,
  SPEEDING: `vehicle_details_speeding`,
  EXCEEDANCE_AVG: `vehicle_details_exceedance_avg`,
}

export type VehicleDetailsProps = {
  vehicle: TripVehicle
}

export const VehicleDetailsComponent = ({ vehicle }: VehicleDetailsProps) => (
  <Box>
    <Flex alignItems="center">
      <SubTitle>Vehicle Details</SubTitle>
    </Flex>
    <Separator size="block" color="yellow" css={styles.marginBottom} />
    <Flex css={styles.detailsContainer}>
      <Box css={styles.marginRight}>
        <Text use="headline2" tag="h4" css={styles.vehicleDetailsHeading}>
          Vehicle
        </Text>
        <Text data-testid={VEHICLE_DETAILS_DTI.VRN}>
          <MaybeObfuscatedValue type="vrn" value={vehicle.vrn} />
        </Text>
        <Flex>
          <Text use="body2" data-testid={VEHICLE_DETAILS_DTI.MAKE_MODEL}>
            {vehicle.name}
          </Text>
        </Flex>
      </Box>
      <Box css={styles.marginRight}>
        <Text use="headline2" tag="h4" css={styles.vehicleDetailsHeading}>
          Vehicle score
        </Text>
        <Score
          score={vehicle.currentPeriod?.score ?? null}
          change={calculateScoreChange(vehicle.currentPeriod?.score, vehicle.previousPeriodScore)}
        />
      </Box>
      <Box css={styles.marginRight}>
        <Text use="headline2" tag="h4" css={styles.vehicleDetailsHeading}>
          Miles driven
        </Text>
        <Text data-testid={VEHICLE_DETAILS_DTI.MILES_DRIVEN}>
          {vehicle.currentPeriod?.distance ? `${vehicle.currentPeriod?.distance} miles` : ``}
        </Text>
      </Box>
      <Box css={styles.marginRight}>
        <Text use="headline2" tag="h4" css={styles.vehicleDetailsHeading}>
          Miles spent speeding
        </Text>
        <Text data-testid={VEHICLE_DETAILS_DTI.SPEEDING}>
          {formatPercentage(vehicle.currentPeriod?.distanceSpentSpeeding, 1)}
        </Text>
      </Box>
      <Box css={styles.marginRight}>
        <Text use="headline2" tag="h4" css={styles.vehicleDetailsHeading}>
          Average Speed exceedance
        </Text>
        <Text data-testid={VEHICLE_DETAILS_DTI.EXCEEDANCE_AVG}>
          {formatPercentage(vehicle.currentPeriod?.averageSpeedExceedencePercentage, 1)}
        </Text>
      </Box>
    </Flex>
  </Box>
)

type Props = {
  vrn: string
}

export const VehicleDetails = ({ vrn }: Props) => {
  const { policy } = usePolicyAndFleet()
  const [res] = useGetTripVehicle({ policyId: policy.id, vrn })
  if (res.result?.tag === `err`) return <ApiError />
  if (res.loading || !res.result)
    return (
      <Box css={styles.loaderAlignment}>
        <CenteredLoader />
      </Box>
    )
  return <VehicleDetailsComponent vehicle={res.result.data} />
}
